import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { cn } from 'v5/platform/dom/cn';
import { WithServices } from 'v5/platform/ts/services';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';

export interface OnlineStatusWidgetProps
  extends WithServices<[WithOnlineStatusService]>,
    React.ComponentPropsWithoutRef<'button'> {}

export const OnlineStatusWidget = forwardRef<
  HTMLButtonElement,
  OnlineStatusWidgetProps
>(function OnlineStatusWidget({ services, className, ...rest }, ref) {
  const { t } = useTranslation('core');
  const status = services.onlineStatusService.useStatus();
  const isOnline = status === 'online';

  const tooltipTitle = isOnline
    ? t('online_status.online')
    : t('online_status.offline');

  return (
    <SimpleTooltip title={tooltipTitle} align="start" delayDuration={500}>
      <button
        ref={ref}
        type="button"
        className={cn(
          'flex items-center justify-center size-8 rounded-md border border-transparent text-fg-default hocus:border-border-muted hocus:bg-bg-subtle/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className
        )}
        aria-label={tooltipTitle}
        {...rest}
      >
        <span
          className={cn(
            'size-2 rounded-full',
            isOnline ? 'bg-bg-success-emphasis' : 'bg-bg-danger-emphasis'
          )}
          aria-hidden
        />
      </button>
    </SimpleTooltip>
  );
});

OnlineStatusWidget.displayName = 'OnlineStatusWidget';
